import * as React from 'react';
import { useCallback, useMemo } from 'react';

import { DropdownAutocomplete } from '@frontierag/frontier-ui-library';
import { useField } from 'formik';

import type { IDropdownAutocompleteField } from './types';

const getOptionId = (option) => (typeof option === 'string' ? option : option?.id);

export default function DropdownAutocompleteField<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>({
  handleChange,
  options,
  textFieldProps,
  ...rest
}: IDropdownAutocompleteField<T, Multiple, DisableClearable, FreeSolo>) {
  const { name = '' } = textFieldProps;

  const [{ value }, metaId, { setValue, setTouched }] = useField(name);
  const hasError = Boolean(metaId.touched && metaId.error);
  const helperText = hasError ? metaId.error : '';

  const handleOnBlur: React.FocusEventHandler<
    HTMLInputElement | HTMLTextAreaElement | HTMLDivElement
  > = useCallback(async () => {
    if (!metaId.touched) await setTouched(true, false);
  }, [setTouched, metaId]);

  const onChange = useCallback(
    async (event, val) => {
      const newVal = getOptionId(val) || '';
      await setValue(newVal, true);
      if (handleChange) handleChange(newVal);
    },
    [handleChange, setValue],
  );

  const selectedValue: any = useMemo(
    () => options.find((option) => getOptionId(option) === value),
    [options, value],
  );

  return (
    <DropdownAutocomplete
      displayPopupIcon
      onChange={onChange}
      textFieldProps={{
        onBlur: handleOnBlur,
        name: name,
        error: hasError,
        helperText: helperText,
        ...textFieldProps,
      }}
      options={options}
      value={selectedValue || null}
      {...rest}
    />
  );
}
